// IMPORTANT: ADD EVENTMETAKEYS HERE OR THEY WONT SHOW UP!!!!!
import moment from 'moment-timezone'
import { TIME_FORMATS } from '@/config/dates.config'
import { EVENTS_DURING_VISIT_WINDOW_DAYS } from '@/config/events.config'
import TXT from '@/messages'
import { isThisYear } from './dates.utils'
import { getClosestOffsetToNow } from '@/utils/transformers.utils'

export const EventMetaKeys = [
  'eventAge',
  'subTitle',
  'showStart',
  // Block of text appears in the EventLocationCard below Ages and above FAQ
  'eventNote',
  'eventNotificationText',
  'supportingActs',
  'exhibitionHours',
  'notLphEligible',
  'shortDescription',
  'externalTicketUrl',
  'externalBtnTxt',
  'imageCover',
  'imageProfile',
  'venue',
  'seated',
  'mwHeaderDescription',
  'tags',
  'eventType',
] as const

export type EventMetaKey = typeof EventMetaKeys[number]

export type EventMeta = Record<EventMetaKey, string>

/**
 * Checks if a specific metadata property exist
 * @param {EventMetaKey} property
 * @param {EventMeta} metaData
 * @returns {boolean} - true if the property exists and has a length greater than 0
 */
export const hasMeta = (property: EventMetaKey, metaData: EventMeta) : boolean => (
  !!metaData[property] && metaData[property].length > 0
)

/**
 * Takes event.meta and venue.meta and returns the alternate venue address if it is present
 * in the event.meta. If event.venue is not present or missing the expected delimiter '|' in Ticketure,
 * the default venue address is returned.
 *
 * @param {EventMeta} meta - event.meta contains the event's alternate venue address
 * @param {VenueWithMeta | Venue} venue - venue.meta contains the event's default address
 *
 * @returns {Object} An object with two properties: venueTitle and venueAddress
 *
 * @example
 * // example input: `event.meta = 'the moon| earth's orbit'`, {...defaultVenueMeta}
 * // expected output: { venueTitle: 'the moon', venueAddress: 'earth's orbit' }
 *
 * @example
 * // example input: `event.meta = 'the moon|'` venue.meta = {...defaultVenueMeta}
 * // expected output: { venueTitle: 'the moon', venueAddress: false }
 *
 * @example
 * // no event.meta or incorrectly formatted
 * // expected output: { venueTitle: defaultVenueTitle, venueAddress: defaultVenueAddress }
 */
export const handleVenueMeta = (
  meta: EventMeta,
  venue: Transformed.VenueWithMeta | Transformed.Venue,
): {
  venueTitle: string
  venueAddress: Nullable<string>
} => {
  const hasAltVenue = hasMeta('venue', meta)
  let venueTitle = venue.title
  let venueAddress = venue.address

  // altVenue exists in EventMeta and contains the expected delimiter
  if (hasAltVenue) {
    // venueAddress will be a string or false
    const splitVenue = meta.venue.split('|')
    venueTitle = splitVenue[0].trim()
    venueAddress = splitVenue[1]?.trim().length > 0
      ? splitVenue[1].trim()
      : null
  }

  return {
    venueTitle,
    venueAddress,
  }
}

/**
 * Convert MetaData[] array to EventMeta object
 * @param {MetaData[]} meta
 * @returns {EventMeta} - formatted event meta object
 */
export const createEventMetaObject = (meta: Transformed.MetaData[]): EventMeta => {
  const initialMeta = EventMetaKeys.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {} as EventMeta)

  return meta.reduce((acc, curr) => {
    const key = curr.metakey as EventMetaKey
    if (EventMetaKeys.includes(key)) {
      acc[key] = curr.value
    }
    return acc
  }, initialMeta)
}

/**
 * @deprecated - event card v2 does this on the back end
 * @param timeslots
 * @param onSaleDate
 * @param meta
 */
export const getBannerText = (
  timeslots: readonly Transformed.Timeslot[],
  onSaleDate: string | undefined,
  meta: EventMeta,
): string | undefined => {
  const eventSoldOut = timeslots.every((ts) => ts.soldOut)
  const lowTicketWarning = timeslots.reduce(
    (previous, current) => {
      if (current.capacity.total > -1) {
        const lowTicketThreshold = current.capacity.total * 0.2
        if (current.capacity.remaining <= lowTicketThreshold) {
          return true
        }
      }
      return previous
    },
    false,
  )

  if (hasMeta('eventNotificationText', meta)) {
    return meta.eventNotificationText
  }

  if (onSaleDate) {
    return onSaleDate
  }

  if (eventSoldOut) {
    return TXT.alerts.eventSoldOut
  }

  if (lowTicketWarning) {
    return TXT.alerts.lowTicketWarning
  }

  return undefined
}

/**
 * Convert MetaData[] array to EventMeta object
 * @param {MetaData[]} meta
 * @returns {EventMeta} - formatted event meta object
 */
export const getEventMeta = (meta: readonly Transformed.MetaData[]): EventMeta => (
  meta.reduce((acc, curr) => {
    const key = curr.metakey as EventMetaKey
    acc[key] = EventMetaKeys.includes(key) ? curr.value : ''
    return acc
  }, {} as EventMeta)
)

/**
 * Formats a single timeslot based on specified type and metadata.
 *
 * @param {Timeslot} timeslot - The timeslot object containing details about the event's start time.
 * @param {string} timeZone - The time zone in which the timeslot should be formatted.
 * @param {EventMeta} meta - Metadata associated with the event, potentially including options like display preferences.
 * @param {'card' | 'details'} type - The type of formatting to apply, either 'card' (for compact event cards)
 *                                    or 'details' (for detailed presentation).
 * @returns {TimeslotDateStringsReturn} Returns an object containing formatted date and time strings:
 *                           - For 'details', includes a formatted date and descriptive time string.
 *                           - For 'card', includes a formatted time string and sets `formattedDate` to null.
 *
 * @deprecated - replaced by transformEvents.utils.ts/formatSingleTimeslotV2 which takes
 * `Ticketure.EventSession` as a prop instead of `Timeslot` and has been moved to the `src/api/transformers/` folder
 */
export const formatSingleTimeslot = (
  timeslot: Transformed.Timeslot,
  timeZone: string,
  meta: EventMeta,
  type: 'card' | 'details',
): Transformed.TimeslotDateStringsReturn => {
  const { startTime } = timeslot
  const startMoment = moment(startTime).tz(timeZone)

  if (type === 'details') {
    const startTimeFormat = startMoment.format(TIME_FORMATS.timeWithAMPM)
    const showStart = hasMeta('showStart', meta) && meta.showStart
    const formattedDate = startMoment.format(TIME_FORMATS.dayNameWithMonthAndDayWithYear)
    const formattedTime = `${startTimeFormat} Doors ${showStart ? `| ${showStart} Show` : ''}`

    return { formattedDate, formattedTime, full: `${formattedDate} ${formattedTime}` }
  }

  // type === 'card'
  let startTimeFormat = TIME_FORMATS.eventCardStart_single
  if (!isThisYear(startMoment, timeZone)) {
    startTimeFormat = TIME_FORMATS.eventCardStartWithYear_single
  }

  const formattedTime = startMoment.format(startTimeFormat)

  return {
    formattedDate: null,
    formattedTime,
    full: formattedTime,
  }
}

/**
 * Formats multiple timeslots into a readable date range string, taking into account whether the
 * timeslots occur over multiple days, years, or within the same year. The resulting format is
 * adjusted based on the event's duration and whether it spans different years or only one day.
 *
 * @param {readonly Timeslot[]} timeslots - An array of timeslot objects, where each object
 *    contains information about the start and end times of an event or session.
 * @param {string} timeZone - The time zone used to format the timeslot start and end times.
 * @returns {TimeslotDateStringsReturn} An object containing the formatted date string (`formattedDate`) and
 *    a placeholder `formattedTime` (currently returning null).
 *
 * @deprecated - replaced by transformEvents.utils.ts/formatMultipleTimeslotsV2 which takes
 * `Ticketure.EventSession[]` as a prop instead of `Timeslot[]` and has been moved to the `src/api/transformers/` folder
 */
export const formatMultipleTimeslots = (
  timeslots: readonly Transformed.Timeslot[],
  timeZone: string,
): Transformed.TimeslotDateStringsReturn => {
  const firstTimeslot = timeslots[0] as Transformed.Timeslot
  const lastTimeslot = timeslots[timeslots.length - 1] as Transformed.Timeslot

  const startMoment = moment(firstTimeslot.startTime).tz(timeZone)
  const endMoment = moment(lastTimeslot.endTime).tz(timeZone)

  const isSingleDayEvent = startMoment.isSame(endMoment, 'day')
  const isSingleYearEvent = startMoment.isSame(endMoment, 'year')
  const isCurrentYear = isThisYear(startMoment, timeZone)

  let formattedDate

  if (!isSingleYearEvent) {
    // Multi-year event (Dec 29, 2022 - Jan 01, 2023)
    const format = TIME_FORMATS.eventCardEndWithYear_multiple
    formattedDate = `${startMoment.format(format)} - ${endMoment.format(format)}`
  } else if (!isSingleDayEvent) {
    // Multi-day event
    const startFormat = TIME_FORMATS.eventCardStart_multiple
    const endFormat = isCurrentYear
      ? TIME_FORMATS.eventCardEnd_multiple
      : TIME_FORMATS.eventCardEndWithYear_multiple

    formattedDate = `${startMoment.format(startFormat)} - ${endMoment.format(endFormat)}`
  } else {
    // Single day event
    formattedDate = startMoment.format(
      isCurrentYear
        ? TIME_FORMATS.eventCardStart_single
        : TIME_FORMATS.eventCardStartWithYear_single,
    )
  }

  return {
    formattedDate,
    formattedTime: null,
    full: formattedDate,
  }
}

// TODO: how would this handle multiple timeslots on the same day?
// TODO: how would this handle events on around new years?
export const getFormattedEventTimeSlots = (
  timeslots: readonly Transformed.Timeslot[],
  timeZone: string,
  meta: EventMeta,
  type: 'card' | 'details',
): Transformed.TimeslotDateStringsReturn => {
  if (timeslots.length === 1) {
    return formatSingleTimeslot(timeslots[0], timeZone, meta, type)
  }

  return formatMultipleTimeslots(timeslots, timeZone)
}

/**
 * Checks whether the provided event is a repeating event and is not marked as multi-day in its metadata.
 *
 * @param {Transformed.EventWithTimeslotsAndMeta} event - The event object to evaluate, containing event type,
 *        timeslot information, and associated metadata.
 * @returns {boolean} Returns true if the event is of type 'repeating' and does not have
 *          a metadata entry where `metakey` is 'eventType' and `value` is 'multi_day';
 *          otherwise, returns false.
 */
export const checkIsRepeatingEvent = (event: Transformed.EventWithTimeslotsAndMeta): boolean => (
  event.eventType === 'repeating'
  && !event.meta.some((meta) => meta?.metakey === 'eventType' && meta?.value === 'multi_day')
)

/**
 * Retrieves events where all ticketCategories.saleWindow elements have active and elapsed set to false
 * if an event meets this criteria, we assume that it is not on sale yet and include it in the returned array.
 *
 * @param events
 * @param { string } timeZone - The timezone for formatting the closest offset.
 * @param timeFormat
 * @returns {{ id: string closestOffset: string}[]} - An array of objects containing the eventId and
 * formatted closest offset (time closest to now) for each event.
 * @deprecated
 */
export const getEventsNotOnSale = (
  events: ReadonlyArray<Transformed.EventWithTimeslotsAndMeta | Transformed.ReducedEventWithTimeslotsAndMeta>,
  timeZone: string,
  timeFormat = TIME_FORMATS.monthAndDayWithYear,
): Array<{
  id: string
  closestOffset: string
}> => events.reduce((result: Array<{ id: string; closestOffset: string }>, event) => {
  const allWindowsInactive = event?.ticketCategories.filter((el) => !el?.meta
    // if an event has addons meta set to '1', filter it out
    .some((meta) => meta?.metakey === 'addons' && meta.value === '1'))
    .every((category) => category?.salesWindow
      .every((window) => !window?.active && !window?.elapsed))

  if (event?.ticketCategories && allWindowsInactive) {
    const now = moment.utc()
    const offsets: string[] = event.ticketCategories
      .flatMap((ticketCat) => (
        ticketCat?.salesWindow
          .filter((window) => !!window && !!window.startOffset)
          .map((window) => (window.startOffset as string))))
    // getClosestOffset gives us the closest onsale date
    const closestOffset = getClosestOffsetToNow(offsets, now)
    if (closestOffset) {
      result.push({
        id: event.id,
        closestOffset: TXT.eventsPage.onSale(moment.utc(closestOffset).tz(timeZone)
          .format(timeFormat)),
      })
    }
  }
  return result
}, [])

const eventCategoryList: ArrayOfUnion<Transformed.EventCategory> = [
  'AddOn',
  'Admission',
  'Community',
  'DayPass',
  'Music',
  'MusicExternal',
  'Workshops',
]

/**
 * Checks if a given value is a valid event category.
 *
 * This function determines whether the provided value is a string
 * and matches one of the predefined event categories within the
 * allowed event category list.
 *
 * @param {unknown} value - The value to validate as an event category.
 * @returns {value is Transformed.EventCategory} - Returns `true` if the value is a valid event category,
 * otherwise `false`.
 */
export const isEventCategory = (value: unknown): value is Transformed.EventCategory => (
  typeof value === 'string'
    && eventCategoryList.includes(value as Transformed.EventCategory)
)

/**
 * Determines the customer's visit date from the line items they purchased.
 *
 * Uses the earliest timeslot start across all line items, since an order can span
 * several timeslots (e.g. admission plus a later add-on) and the visit begins at
 * the first one. Timeslot start times are ISO-8601, which sorts lexicographically.
 *
 * @param {readonly Transformed.LineItem[]} lineItems - The purchased line items.
 * @returns {Nullable<string>} The earliest timeslot start time, or `null` when no line
 * item is timed (gift orders and add-on-only carts have no timeslot).
 */
export const getVisitDateFromLineItems = (
  lineItems: readonly Transformed.LineItem[],
): Nullable<string> => {
  const startTimes = lineItems
    .map((lineItem) => lineItem.timeslot?.startTime)
    .filter((startTime): startTime is string => !!startTime)
    .toSorted()

  return startTimes[0] ?? null
}

/**
 * Builds the session window worth promoting to a customer around the time of their visit.
 *
 * The window covers whole calendar days in the seller's timezone, so `windowDays` of 3
 * spans seven days: the visit date plus three either side. When the order has no visit
 * date the window is anchored on now instead, degrading to "events happening in the next
 * few days".
 *
 * The bounds are handed to the events query rather than used to filter in memory, so the
 * API only returns the events that are actually promotable. Both are inclusive, so a
 * session starting exactly at the first local midnight still counts as in the window.
 *
 * @param {Object} args - The window arguments.
 * @param {Nullable<string>} args.visitDate - The visit date to anchor on, or `null` for now.
 * @param {string} args.timezone - The seller's timezone.
 * @param {number} [args.windowDays] - Days either side of the anchor to include.
 * @returns {DataAccessInput.EventsSessionWindow} ISO-8601 bounds for the events query.
 */
export const getVisitSessionWindow = ({
  visitDate,
  timezone,
  windowDays = EVENTS_DURING_VISIT_WINDOW_DAYS,
}: {
  visitDate: Nullable<string>
  timezone: string
  windowDays?: number
}): Required<DataAccessInput.EventsSessionWindow> => {
  const anchor = visitDate ? moment.tz(visitDate, timezone) : moment.tz(timezone)

  return {
    startDateTime: anchor.clone()
      .subtract(windowDays, 'days')
      .startOf('day')
      .toISOString(),
    endDateTime: anchor.clone()
      .add(windowDays, 'days')
      .endOf('day')
      .toISOString(),
  }
}
